import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { Heart, Mic, Play, Trash2, PenLine, Volume2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import EnhancedVoiceRecorder from '@/components/EnhancedVoiceRecorder';
interface LifelineItem {
  id: string;
  type: 'lifeline_letter' | 'lifeline_voice';
  content: string;
  caption?: string;
  created_at: string;
}
interface LifelineLettersProps {
  user: any;
  onBack: () => void;
}
export const LifelineLetters = ({
  user,
  onBack
}: LifelineLettersProps) => {
  const [items, setItems] = useState<LifelineItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItemType, setNewItemType] = useState<'lifeline_letter' | 'lifeline_voice'>('lifeline_letter');
  const [newContent, setNewContent] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  useEffect(() => {
    loadItems();
  }, [user.id]);
  const loadItems = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('hope_kit' as any).select('*').eq('user_id', user.id).in('type', ['lifeline_letter', 'lifeline_voice']).order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setItems(data as any || []);
    } catch (error) {
      console.error('Error loading lifeline items:', error);
      toast.error('Failed to load your lifeline items');
    } finally {
      setIsLoading(false);
    }
  };
  const addItem = async () => {
    if (!newContent.trim()) {
      toast.error('Please add some content');
      return;
    }
    try {
      const {
        error
      } = await supabase.from('hope_kit' as any).insert({
        user_id: user.id,
        type: newItemType,
        content: newContent,
        caption: newCaption || null
      });
      if (error) throw error;
      toast.success('Lifeline saved successfully');
      setNewContent('');
      setNewCaption('');
      setShowAddForm(false);
      loadItems();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to save lifeline');
    }
  };
  const deleteItem = async (id: string) => {
    try {
      const {
        error
      } = await supabase.from('hope_kit' as any).delete().eq('id', id);
      if (error) throw error;
      toast.success('Item deleted');
      loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };
  const handleVoiceRecordingComplete = async (audioBlob: Blob) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Audio = reader.result as string;
      setNewContent(base64Audio);
      await addItem();
    };
    reader.readAsDataURL(audioBlob);
  };
  const playAudio = (audioData: string, itemId: string) => {
    if (playingAudio === itemId) {
      setPlayingAudio(null);
      return;
    }
    const audio = new Audio(audioData);
    audio.onended = () => setPlayingAudio(null);
    audio.play();
    setPlayingAudio(itemId);
  };
  const prompts = ["Think about someone or something you love deeply - a person, pet, passion, or purpose...", "What future moment are you looking forward to? A goal, dream, or simple pleasure?", "Remember a time you overcame something difficult. What strengths did you use?", "What would you tell a friend going through what you're experiencing right now?", "What small act of self-care or joy could you commit to trying tomorrow?"];
  if (isLoading) {
    return <div className="text-center py-8">Loading your lifelines...</div>;
  }
  return <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Crisis Suite
      </Button>

      <Card className="p-6 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
        <div className="flex items-start gap-4">
          <Heart className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
          <div className="space-y-3">
            <h2 className="text-2xl font-semibold text-foreground">Your Lifeline Letters</h2>
            <div className="text-muted-foreground space-y-2">
              <p>Just as we prepare medicine before we become unwell, these messages are a way of preparing emotional care in advance. Write at letter to yourself or record a voice note with the reassurance, reminders, or compassion you may need when things feel difficult.</p>
              <p className="italic text-primary">When we have a cold, we expect symptoms like a sore throat or a cough - we don't see them as permanent, we simply allow time and care for healing. Emotional pain is similar. Difficult thoughts and feelings are symptoms of something the mind and body are processing, not a reflection of who you are or how life will always feel. These reminders are here to steady you until the symptoms ease and you return to balance again.</p>
            </div>
          </div>
        </div>
      </Card>

      {!showAddForm && <div className="grid gap-3 md:grid-cols-2">
          <Button onClick={() => {
        setNewItemType('lifeline_letter');
        setShowAddForm(true);
      }} className="h-auto py-6 flex flex-col items-center gap-2" variant="outline">
            <PenLine className="h-6 w-6" />
            <span>Write a Letter to Yourself</span>
          </Button>
          <Button onClick={() => {
        setNewItemType('lifeline_voice');
        setShowAddForm(true);
      }} className="h-auto py-6 flex flex-col items-center gap-2" variant="outline">
            <Mic className="h-6 w-6" />
            <span>Record a Voice Note</span>
          </Button>
        </div>}

      {showAddForm && <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              {newItemType === 'lifeline_letter' ? <>
                  <PenLine className="h-5 w-5" />
                  Write Your Letter
                </> : <>
                  <Mic className="h-5 w-5" />
                  Record Your Message
                </>}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => {
          setShowAddForm(false);
          setNewContent('');
          setNewCaption('');
        }}>
              Cancel
            </Button>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
            <p className="font-medium">Consider including:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>People, pets, or passions that matter to you</li>
              <li>Future moments you want to experience</li>
              <li>Times you've overcome difficulties before</li>
              <li>Small joys or goals to look forward to tomorrow</li>
              <li>Your purpose, values, or what makes life meaningful</li>
            </ul>
          </div>

          {newItemType === 'lifeline_letter' ? <>
              <div>
                <label className="text-sm font-medium mb-2 block">Title (optional)</label>
                <input type="text" value={newCaption} onChange={e => setNewCaption(e.target.value)} placeholder="e.g., 'Why I Want to Be Here' or 'Letter to Future Me'" className="w-full px-3 py-2 border rounded-md bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Your Message</label>
                <Textarea value={newContent} onChange={e => setNewContent(e.target.value)} placeholder="Dear me, I want you to know..." rows={8} className="resize-none" />
              </div>
              <Button onClick={addItem} className="w-full">
                <Heart className="h-4 w-4 mr-2" />
                Save Letter
              </Button>
            </> : <>
              <div>
                <label className="text-sm font-medium mb-2 block">Title (optional)</label>
                <input type="text" value={newCaption} onChange={e => setNewCaption(e.target.value)} placeholder="e.g., 'Reasons I'm Here' or 'My Voice of Hope'" className="w-full px-3 py-2 border rounded-md bg-background" />
              </div>
              <EnhancedVoiceRecorder onRecordingComplete={handleVoiceRecordingComplete} maxDuration={180} />
            </>}
        </Card>}

      {!showAddForm && prompts.length > 0 && items.length === 0 && <Card className="p-4">
          <h4 className="text-sm font-semibold mb-3">Need inspiration?</h4>
          <div className="space-y-2">
            {prompts.map((prompt, index) => <button key={index} onClick={() => {
          setNewItemType('lifeline_letter');
          setShowAddForm(true);
        }} className="w-full text-left p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-sm">
                {prompt}
              </button>)}
          </div>
        </Card>}

      <div className="space-y-4">
        {items.length === 0 ? <Card className="p-8 text-center">
            <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              You haven't created any lifeline letters or voice notes yet.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Create them now so they're ready when you need them most.
            </p>
          </Card> : items.map(item => <Card key={item.id} className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {item.type === 'lifeline_letter' ? <PenLine className="h-5 w-5 text-primary" /> : <Volume2 className="h-5 w-5 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  {item.caption && <h4 className="font-semibold mb-2">{item.caption}</h4>}
                  {item.type === 'lifeline_letter' ? <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                      {item.content}
                    </p> : <Button onClick={() => playAudio(item.content, item.id)} variant="outline" size="sm" className="mt-2">
                      <Play className="h-4 w-4 mr-2" />
                      {playingAudio === item.id ? 'Playing...' : 'Play Voice Note'}
                    </Button>}
                  <p className="text-xs text-muted-foreground mt-2">
                    Created {new Date(item.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)} className="flex-shrink-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>)}
      </div>
    </div>;
};